import React from "react";
import { Fabric, Theme, initializeIcons } from "@nuorder/components";
import { FolderSelection } from "./folder-selection";
import { IFolderSelectionSettings } from "./use-folder-selection";
import { useFetchMembershipForUser } from "containers/folders/hooks/merchants";
import me from "models/me";
import { useFetchItem, useFetchLineageItem } from "containers/folders/hooks/space-item";
import { formatMessage } from "lib/globalize-util";
import { IFolderSelection } from "./folder-navigation";
import { shouldRetryHookOnError, SpaceView } from "containers/folders/hooks/utils";
import { useFetchBrandForMerchant } from "containers/folders/hooks/brands";
import { MAX_FAILURE_COUNT, MAX_LINEAGE_CALLS } from "containers/folders/utils/constants";
import { buildRootDrive } from "containers/folders/utils/drive-navigation";
import { useFetchSpaces } from "containers/folders/hooks/useFetchSpaces";
import { getParentItem, getParentItemWithWriteAccess } from "../utils/folder-selection";

interface IAssortmentManageAccessPanelProps {
    item: nuorder.folder.v1.Item;
    displayMerchantDrive: boolean;
    membership: nuorder.merchant.v1.Member;
    mySpace: nuorder.folder.v1.Space;
    merchantSpace: nuorder.folder.v1.Space;
    settings: IFolderSelectionSettings;
    onUpdateSettings: (settings: Partial<IFolderSelectionSettings>) => void;
    displayMyDrive: boolean;
}

initializeIcons();

export const useInitializeAssortmentFolderSelection = (
    merchantId: string,
    item: nuorder.folder.v1.Item,
    onNavigationFetched: (navigation: IFolderSelection[], view: SpaceView) => void,
    onlyLineage: boolean = false // By default if you don't have access to parent folder it redirects to my drive and this disable that
) => {
    const { data: membership } = useFetchMembershipForUser(merchantId, me._id, {
        enabled: !!merchantId,
    });
    const { merchantSpace, mySpace, fetchingMerchantSpaces, isIdleMerchantSpaces, fetchingMySpaces, isIdleMySpaces } =
        useFetchSpaces(membership?.merchant_id, true);
    const isBrandUser = me.isBrandUser();
    const { data: brand } = useFetchBrandForMerchant(membership?.merchant_id, true, {
        retry: (failureCount: number, err: any) => {
            if (!shouldRetryHookOnError(err)) {
                return false;
            }
            return failureCount <= MAX_FAILURE_COUNT;
        },
        enabled: !!membership?.merchant_id && isBrandUser,
    });

    const merchantSpaceFetched = !isIdleMerchantSpaces && !fetchingMerchantSpaces;
    const mySpaceFetched = !isIdleMySpaces && !fetchingMySpaces;
    const isSharedItem = isBrandUser
        ? true
        : merchantSpaceFetched && mySpaceFetched && merchantSpace?.id !== item.space && mySpace?.id !== item.space;

    const [currentParent, setCurrentParent] = React.useState<IFolderSelection>(null);
    // isSharedItem at root level the next line should return 403
    useFetchItem(merchantId, item?.parent, true, {
        enabled:
            !!merchantId && !!item?.parent && ((!isBrandUser && merchantSpaceFetched && mySpaceFetched) || isBrandUser),
        retry: (failureCount: number, err: any) => {
            if (!shouldRetryHookOnError(err)) {
                return false;
            }
            return failureCount <= MAX_FAILURE_COUNT;
        },
        onError: (err) => {
            if (isSharedItem && (err as any).code === 403) {
                setCurrentParent({
                    name: formatMessage("Shared"),
                    spaceView: SpaceView.SharedWithMe,
                    isDrive: true,
                });

                if (onlyLineage) {
                    const navigation = buildRootDrive(null, SpaceView.SharedWithMe);
                    onNavigationFetched([navigation], SpaceView.SharedWithMe);
                }
            }
        },
        onSuccess: (data) => {
            setCurrentParent(data as IFolderSelection);
        },
    });

    const parentItem = React.useMemo(() => {
        if (!merchantSpaceFetched || !mySpaceFetched || !currentParent) {
            return null;
        }

        return getParentItem(currentParent, merchantSpace, mySpace, membership);
    }, [merchantSpace, mySpace, currentParent, merchantSpaceFetched, mySpaceFetched, membership]);

    const canListChildren = React.useMemo(() => {
        if (!merchantSpace?.capabilities) {
            return !merchantSpaceFetched ? null : false;
        }

        return merchantSpace.capabilities.can_list_children;
    }, [merchantSpace, merchantSpaceFetched]);

    const parentItemWithAccess =
        onlyLineage && isSharedItem ? parentItem : getParentItemWithWriteAccess(parentItem, mySpace);
    const space =
        merchantSpace?.id === parentItemWithAccess?.space
            ? merchantSpace
            : mySpace?.id === parentItemWithAccess?.space
              ? mySpace
              : null;
    const { isFetching: isFetchingLineage } = useFetchLineageItem(
        merchantId,
        parentItemWithAccess?.id,
        isSharedItem ? null : space?.root_item_id,
        MAX_LINEAGE_CALLS,
        true,
        {
            enabled: !!merchantId && !!parentItemWithAccess?.id && merchantSpaceFetched && mySpaceFetched,
            retry: (failureCount: number, err: any) => {
                if (!shouldRetryHookOnError(err)) {
                    return false;
                }
                return failureCount <= MAX_FAILURE_COUNT;
            },
            onSettled: (response: IFolderSelection[], error: any) => {
                const view =
                    merchantSpace?.id === parentItemWithAccess.space
                        ? SpaceView.MerchantDrive
                        : mySpace?.id === parentItemWithAccess.space
                          ? SpaceView.MyDrive
                          : SpaceView.SharedWithMe;
                const navigation = buildRootDrive(
                    space,
                    view,
                    membership?.merchant_ref?.name ?? membership?.merchant_id
                );

                if (error) {
                    onNavigationFetched([navigation], view);
                } else {
                    onNavigationFetched([navigation].concat(response), view);
                }
            },
        }
    );

    return {
        displayMerchantDrive: canListChildren,
        merchantSpace,
        mySpace,
        parentItem: parentItemWithAccess,
        membership,
        isFetchingLineage,
    };
};

export const AssortmentFolderSelection: React.FC<IAssortmentManageAccessPanelProps> = ({
    item,
    displayMerchantDrive,
    membership,
    mySpace,
    merchantSpace,
    displayMyDrive,
    settings,
    onUpdateSettings,
}) => {
    return (
        <Theme>
            <Fabric>
                <FolderSelection
                    displayMerchantDrive={displayMerchantDrive}
                    displayMyDrive={displayMyDrive}
                    membership={membership}
                    mySpace={mySpace}
                    merchantSpace={merchantSpace}
                    item={item}
                    settings={settings}
                    onUpdateSettings={onUpdateSettings}
                />
            </Fabric>
        </Theme>
    );
};
